import React from 'react'
import { Button, Typography } from 'antd'
import {
  MdStar,
  MdFlightTakeoff,
  MdWork,
  MdAssessment,
  MdTrendingUp,
  MdSchool,
  MdBusiness,
  MdLocalHospital,
  MdComputer,
  MdArrowForward,
  MdPeople,
  MdCardMembership,
  MdVerifiedUser,
} from 'react-icons/md'
import styled, { createGlobalStyle } from 'styled-components'
import { useHistory } from 'react-router-dom'
import PublicPageLayout from '../../components/PublicPageLayout'

const { Text, Paragraph, Title } = Typography

/* ── GLOBAL ── */
const GlobalPageStyle = createGlobalStyle`
  body {
    background: #fff;
    margin: 0;
    padding: 0;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #333;
  }
`


/* ══════════════════════════════════════════════
   CAREERS PAGE SPECIFIC STYLES
══════════════════════════════════════════════ */
const PageBg = styled.div`
  background: #f4f7f9;
  min-height: 80vh;
`

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 16px;
`

/* Hero section - matches Alison style */
const HeroBanner = styled.div`
  position: relative;
  background: ${p => p.bg ? `url(${p.bg}) center/cover no-repeat` : 'white'};
  border-radius: 12px;
  padding: 70px 56px;
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  overflow: hidden;
  border: ${p => p.bg ? 'none' : '1px solid #e5e7eb'};
  min-height: 380px;

  ${p => p.bg && `
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(10,20,60,0.85) 0%, rgba(10,20,60,0.55) 60%, rgba(10,20,60,0.2) 100%);
    }
  `}

  .hero-text {
    flex: 1;
    position: relative;
    z-index: 1;
    max-width: 600px;

    .badge {
      display: inline-flex;
      align-items: center;
      background: ${p => p.bg ? 'rgba(255,255,255,0.18)' : '#e8f5e9'};
      color: ${p => p.bg ? 'white' : '#2e7d32'};
      border: ${p => p.bg ? '1px solid rgba(255,255,255,0.35)' : 'none'};
      backdrop-filter: ${p => p.bg ? 'blur(6px)' : 'none'};
      font-size: 12px;
      font-weight: 700;
      padding: 4px 14px;
      border-radius: 20px;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 20px;
    }

    h1 {
      font-size: 2.8rem;
      font-weight: 900;
      color: ${p => p.bg ? 'white' : '#111827'};
      line-height: 1.15;
      margin-bottom: 20px;
      letter-spacing: -0.5px;
      text-shadow: ${p => p.bg ? '0 2px 12px rgba(0,0,0,0.4)' : 'none'};
    }

    p {
      font-size: 1.1rem;
      color: ${p => p.bg ? 'rgba(255,255,255,0.88)' : '#4b5563'};
      line-height: 1.7;
      margin-bottom: 32px;
    }

    .cta-row {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
    }

    .cta-primary {
      height: 50px;
      padding: 0 32px;
      background: hsl(33, 100%, 50%);
      border: none;
      border-radius: 6px;
      color: white;
      font-size: 15px;
      font-weight: 700;
      cursor: pointer;
      box-shadow: 0 4px 20px rgba(255,140,0,0.4);
      transition: all 0.2s;
      &:hover { opacity: 0.9; transform: translateY(-1px); }
    }

    .cta-secondary {
      height: 50px;
      padding: 0 32px;
      background: ${p => p.bg ? 'rgba(255,255,255,0.15)' : 'transparent'};
      border: 2px solid ${p => p.bg ? 'rgba(255,255,255,0.6)' : 'hsl(210, 55%, 22%)'};
      backdrop-filter: ${p => p.bg ? 'blur(6px)' : 'none'};
      border-radius: 6px;
      color: ${p => p.bg ? 'white' : 'hsl(210, 55%, 22%)'};
      font-size: 15px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s;
      &:hover { background: ${p => p.bg ? 'rgba(255,255,255,0.28)' : 'hsl(210, 55%, 22%)'}; color: white; }
    }
  }
`

const StatsBand = styled.div`
  display: flex;
  gap: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 40px;
  overflow: hidden;
  .stat-item {
    flex: 1;
    padding: 22px 28px;
    border-right: 1px solid #e5e7eb;
    &:last-child { border-right: none; }
    .num { font-size: 26px; font-weight: 900; color: hsl(210, 55%, 22%); }
    .lbl { font-size: 13px; color: #6b7280; margin-top: 3px; }
  }
`

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 24px;
  margin-top: 0;
`

const PathGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 48px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`

const PathCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 28px 24px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.25s;

  &:hover {
    border-color: hsl(210, 55%, 22%);
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
    transform: translateY(-4px);
  }

  .path-icon {
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: ${p => p.bg || '#e8f5e9'};
    color: ${p => p.color || '#2e7d32'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    margin-bottom: 18px;
  }

  h4 {
    font-size: 16px;
    font-weight: 800;
    color: #111827;
    margin-bottom: 8px;
  }

  p {
    font-size: 13px;
    color: #6b7280;
    line-height: 1.6;
    margin-bottom: 18px;
  }

  .card-link {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    color: hsl(210, 55%, 22%);
    text-decoration: none;
    &:hover { color: hsl(33, 100%, 50%); }
  }
`

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 48px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`

const FeatureCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 32px 24px;
  border: 1px solid #e5e7eb;
  text-align: center;
  transition: all 0.25s;

  &:hover {
    box-shadow: 0 8px 24px rgba(0,0,0,0.07);
    transform: translateY(-3px);
    border-color: hsl(33, 100%, 50%);
  }

  .feat-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #fff8e1;
    color: hsl(33, 100%, 50%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 30px;
    margin: 0 auto 20px;
  }

  h4 { font-size: 16px; font-weight: 800; color: #111827; margin-bottom: 10px; }
  p { font-size: 13px; color: #6b7280; line-height: 1.6; margin: 0; }
`

const CtaBanner = styled.div`
  background: hsl(210, 55%, 22%);
  border-radius: 12px;
  padding: 48px 48px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 48px;
  gap: 32px;

  .cta-text {
    h2 { font-size: 28px; font-weight: 900; color: white; margin-bottom: 12px; line-height: 1.2; }
    p { font-size: 15px; color: rgba(255,255,255,0.8); margin: 0; max-width: 540px; }
  }

  .cta-actions {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
    flex-wrap: wrap;
  }

  .btn-white {
    height: 50px;
    padding: 0 32px;
    background: white;
    border: none;
    border-radius: 6px;
    color: hsl(210, 55%, 22%);
    font-weight: 800;
    font-size: 15px;
    cursor: pointer;
    transition: opacity 0.2s;
    &:hover { opacity: 0.9; }
  }

  .btn-outline {
    height: 50px;
    padding: 0 32px;
    background: transparent;
    border: 2px solid rgba(255,255,255,0.5);
    border-radius: 6px;
    color: white;
    font-weight: 700;
    font-size: 15px;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { border-color: white; background: rgba(255,255,255,0.1); }
  }

  @media (max-width: 768px) { flex-direction: column; }
`

const PATHS = [
  {
    title: 'Information Technology',
    desc: 'Master coding, data science, and cybersecurity to lead the digital revolution.',
    icon: <MdComputer />,
    bg: '#e3f2fd', color: '#1565c0',
  },
  {
    title: 'Business & Finance',
    desc: 'Develop the leadership, financial, and strategic skills to run high-performing organizations.',
    icon: <MdBusiness />,
    bg: '#fff3e0', color: '#e65100',
  },
  {
    title: 'Health & Wellness',
    desc: 'Gain critical medical and healthcare skills that contribute to global health improvements.',
    icon: <MdLocalHospital />,
    bg: '#fce4ec', color: '#c62828',
  },
  {
    title: 'Project Management',
    desc: 'Learn to plan, execute and deliver complex projects on time and within budget.',
    icon: <MdAssessment />,
    bg: '#e8eaf6', color: '#283593',
  },
  {
    title: 'Sales & Marketing',
    desc: 'Drive revenue and brand growth with proven digital marketing strategies and tools.',
    icon: <MdTrendingUp />,
    bg: '#e8f5e9', color: '#2e7d32',
  },
  {
    title: 'Teaching & Education',
    desc: 'Gain internationally recognized teaching qualifications and advance your education career.',
    icon: <MdSchool />,
    bg: '#f3e5f5', color: '#6a1b9a',
  },
]

const FEATURES = [
  {
    title: 'Globally Recognized Certificates',
    desc: 'Earn specialized certifications top employers worldwide actively look for on your resume.',
    icon: <MdVerifiedUser />,
  },
  {
    title: 'Industry Expert Instructors',
    desc: 'Learn directly from professionals with real-world experience in your chosen field.',
    icon: <MdPeople />,
  },
  {
    title: 'Career Development Programs',
    desc: 'Step-by-step vocational training tailored to the exact requirements of your target role.',
    icon: <MdFlightTakeoff />,
  },
  {
    title: 'Direct Employer Access',
    desc: 'Complete your profile to get matched with recruiters and hiring managers actively recruiting.',
    icon: <MdWork />,
  },
  {
    title: 'Free Diploma Programs',
    desc: 'Access 200+ diploma programs absolutely free. No financial barriers to your education.',
    icon: <MdCardMembership />,
  },
  {
    title: 'Star Rated Courses',
    desc: 'Every course is rated by real learners ensuring you only access the highest quality content.',
    icon: <MdStar />,
  },
]

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const PublicCareers = () => {
  const history = useHistory()

  return (
    <PublicPageLayout activeLink="about">
      <GlobalPageStyle />

      {/* ═══ PAGE CONTENT ═══ */}
      <PageBg>
        <ContentWrapper>

          {/* Hero Banner */}
          <HeroBanner bg="/heroes/careers.png">
            <div className="hero-text">
              <h1>Accelerate Your Professional Career</h1>
              <p>
                Build your resume, earn world-class certificates, and connect
                directly with hiring managers actively recruiting on Wise Academy.
              </p>
              <div className="cta-row">
                <button className="cta-primary" onClick={() => history.push('/explore-courses')}>
                  Browse Career Paths
                </button>
                <button className="cta-secondary" onClick={() => history.push('/register')}>
                  Create Free Account
                </button>
              </div>
            </div>
          </HeroBanner>

          {/* Stats Band */}
          <StatsBand>
            <div className="stat-item"><div className="num">4,000+</div><div className="lbl">Career Courses</div></div>
            <div className="stat-item"><div className="num">15M+</div><div className="lbl">Learners Worldwide</div></div>
            <div className="stat-item"><div className="num">200+</div><div className="lbl">Diploma Programs</div></div>
            <div className="stat-item"><div className="num">95%</div><div className="lbl">Employment Rate</div></div>
            <div className="stat-item"><div className="num">100%</div><div className="lbl">Free Registration</div></div>
          </StatsBand>

          {/* Career Paths */}
          <SectionTitle>Explore Popular Career Paths</SectionTitle>
          <PathGrid>
            {PATHS.map((path, i) => (
              <PathCard key={i} bg={path.bg} color={path.color} onClick={() => history.push('/explore-courses')}>
                <div className="path-icon">{path.icon}</div>
                <h4>{path.title}</h4>
                <p>{path.desc}</p>
                <span className="card-link">View Courses <MdArrowForward /></span>
              </PathCard>
            ))}
          </PathGrid>

          {/* Why WISE */}
          <SectionTitle>Why Choose WISE Academy for Your Career?</SectionTitle>
          <FeatureGrid>
            {FEATURES.map((feat, i) => (
              <FeatureCard key={i}>
                <div className="feat-icon">{feat.icon}</div>
                <h4>{feat.title}</h4>
                <p>{feat.desc}</p>
              </FeatureCard>
            ))}
          </FeatureGrid>

          {/* Final CTA */}
          <CtaBanner>
            <div className="cta-text">
              <h2>Ready to Build Your Dream Career?</h2>
              <p>
                Join over 15 million learners on Wise Academy. Create your free account in under 60 seconds
                and start your journey toward your ideal career today.
              </p>
            </div>
            <div className="cta-actions">
              <button className="btn-white" onClick={() => history.push('/register')}>
                Get Started — It's Free
              </button>
              <button className="btn-outline" onClick={() => history.push('/explore-courses')}>
                Browse Courses
              </button>
            </div>
          </CtaBanner>

        </ContentWrapper>
      </PageBg>

    </PublicPageLayout>
  )
}

export default PublicCareers
